import type Merch from '../merch'
import { findParentPath } from '../../utils'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import type MyArticle from '@/models/myArticle'
import utils from '@/services/utils'
import { merchConstants } from '@/models/constants'
import developmentReviewImage from '@/assets/templateLayoutImages/developmentReview.png'

class DevelopmentReview implements IMerchTemplate {
  id: number = merchConstants.slideGenTemplatesId.developmentReview
  name = 'Development Review'
  layoutImage = developmentReviewImage
  slideWidth: number = 1280
  slideHeight: number = 720
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Neue Plak'
  imageDefaultScaleFactor: number = merchConstants.slideImageDefaultScaleFactors.large
  borderProps = {
    height: 24,
    color: '#c82a26',
  }

  slideTitleProps = {
    title: 'SS22 DEVELOPMENT REVIEW',
    fontSize: 26,
    height: 25,
    fontWeight: 'bold',
  }

  slideSubTitleProps = {
    fontSize: 26,
    height: 25,
    fontWeight: 'normal',
    attributes: ['Collection'],
  }

  modelInfoProps = {
    fontSize: 21,
    height: 25,
    fontWeight: 'normal',
  }

  heightTillModelInfo = 250 // if the fixed height till the model is rendered
  staticDataProp = {
    height: 200,
    width: 400,
    color: '#c82a26',
  }

  logoWidth: number = 192
  logoHeight: number = 121
  logoScaling: number = 0.6
  numberOfArticleImagesInOneRow: number = 4
  headerPadding: number = 30
  maxArticlesInOneRow: number = 15
  imageSize: number = 200
  toFitScale: number = 1
  scale: number = 1
  currentTop: number = 0
  isMerch: boolean = true
  needOverLapping: boolean = false
  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>): IMerchTemplateOption[] {
    const attributes: IMyAttribute[] = []
    for (const key in myAttributes) {
      attributes.push(myAttributes[key])
    }

    // TODO: Implement configurable options
    const options: IMerchTemplateOption[] = []

    options.push({
      name: 'sortByExcelSequence',
      label: 'generateDialog.steps.options.sortByExcelSequence',
      type: 'bool',
      default: false,
      required: false,
    })

    options.push({
      name: 'sortByListSequence',
      label: 'generateDialog.steps.options.sortByListSequence',
      type: 'bool',
      default: false,
      required: false,
    })

    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: 'firstImage',
      data: merchConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })
    const availableSlideSizes: IKeyLabel[] = []
    for (const key in merchConstants.slideSize) {
      availableSlideSizes.push({ key, label: merchConstants.slideSize[key] })
    }
    options.push({
      name: 'slideSize',
      label: 'generateDialog.steps.options.slideSize',
      type: 'list',
      default: 'wideScreen',
      required: true,
      multiple: false,
      data: availableSlideSizes,
      valueProp: 'key',
      displayProp: 'label',
    })

    return options
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    const mapping: IMerchTemplateFileMapping[] = []

    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code', 'colorwaynumber', 'colorway number'],
    })
    return mapping
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined): Promise<(void | number)> {
    return await this.buildTemplate(catalog, articlesData, options, merch)
  }

  changePropsAsPerSlideSize(slideSize) {
    this.slideHeight = merchConstants.slideSizeCanvasDimensions[slideSize].height
    this.slideWidth = merchConstants.slideSizeCanvasDimensions[slideSize].width
    if (slideSize === merchConstants.slideSize.wideScreen) {
      this.numberOfArticleImagesInOneRow = 4
    }
    else if (slideSize === merchConstants.slideSize.A3) {
      this.numberOfArticleImagesInOneRow = 5
    }
    else if (slideSize === merchConstants.slideSize.A4) {
      this.numberOfArticleImagesInOneRow = 4
    }
  }

  resetProps(slideSize: string) {
    this.imageSize = 200
    this.maxArticlesInOneRow = 15
    if (slideSize === merchConstants.slideSize.wideScreen) {
      this.toFitScale = 1.6
      this.scale = 1.6
    }
    else if (slideSize === merchConstants.slideSize.A3) {
      this.toFitScale = 1.6
      this.scale = 1.6
    }
    else if (slideSize === merchConstants.slideSize.A4) {
      this.toFitScale = 1.4
      this.scale = 1.4
    }
  }

  buildTemplate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined) {
    this.slideTitleProps.title = `${catalog.Season} DEVELOPMENT REVIEW`
    const slideSize = options.slideSize
    const promises: Promise<void | number>[] = []
    let ttlSlides = 0
    const currentObjects: any[] = []
    this.imageDefaultScaleFactor = merchConstants.slideImageDefaultScaleFactors.large
    const sortByListSequence = options.sortByListSequence
    const sortByExcelSequence = options.sortByExcelSequence
    // Set slide size as per user selected
    this.changePropsAsPerSlideSize(slideSize)
    const modelNumberMap = new Map()
    let previousModelNumber = ''
    articlesData.forEach((article, index) => {
      let currentModelNumber = article.ModelNumber
      // check if previous row modelNumber is same as current row sep  if sortByExcelSequence is true
      if (((sortByExcelSequence || sortByListSequence) && previousModelNumber.includes(currentModelNumber)) || ((!sortByExcelSequence || !sortByListSequence) && modelNumberMap.has(currentModelNumber))) {
        if (sortByExcelSequence || sortByListSequence) {
          currentModelNumber = previousModelNumber
        }
        modelNumberMap.get(currentModelNumber).push(article)
      }
      else {
        if (sortByExcelSequence || sortByListSequence) {
          // add suffix to avoid adding articles that belong to this group but positioned in excel such a way that they should be visible after set of group
          currentModelNumber += index
        }
        modelNumberMap.set(currentModelNumber, [article])
      }
      // use in case of when sortByExcelSequence or sortByListSequence is true only
      previousModelNumber = currentModelNumber
    })
    modelNumberMap.forEach((articles) => {
      ttlSlides++
      this.currentTop = 0
      articles = articles.slice(0, 30)
      this.onNewPage(currentObjects, articles[0])
      this.currentTop = this.logoHeight * this.logoScaling + this.headerPadding
      currentObjects.push(...this.addModelDetails(articles[0], this.currentTop))
      this.currentTop = this.heightTillModelInfo
      this.resetProps(slideSize)
      if (articles.length > this.numberOfArticleImagesInOneRow) {
        this.findTheScalingFactor(articles.length > this.maxArticlesInOneRow ? this.maxArticlesInOneRow : articles.length, this.scale, this.slideWidth)
        this.imageSize = this.toFitScale * this.imageSize
      }
      else {
        this.imageSize = this.scale * this.imageSize
      }
      const currentArticlesInOneRow = articles.length > this.maxArticlesInOneRow ? this.maxArticlesInOneRow : articles.length
      const imagesLeft = this.findLeftForImages(currentArticlesInOneRow)
      let left = imagesLeft
      articles.forEach((article, index) => {
        if (index % (currentArticlesInOneRow) === 0) {
          if (index !== 0) {
            this.currentTop += this.findEachRowHeight()
          }
          left = imagesLeft
        }
        else {
          if (index !== 0) {
            left += this.imageSize
          }
        }
        currentObjects.push(this.createImage(article.Id, options.imageType, left, this.currentTop))
        currentObjects.push(this.createLabel(this.currentTop + this.imageSize, left, ['Colorway', 'ColorwayCode'], this.imageSize, 30, article, this.getFontSize(21), 'normal', false, 'center'))
      })
      if (this.isMerch && utils.isDefined(merch)) {
        promises.push(this.createSlide(merch, articles[0].ArticleName, currentObjects.slice(), options, articles.map(article => article.Id))
          .catch((error) => {
          // TODO:Merch@andre ask andre what needs to be done when failed creating slide due to system unable to create slide objects file
            console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
          }))
      }
    })
    return Promise.all(promises)
      .then(() => {
        return ttlSlides
      })
  }

  findEachRowHeight() {
    return (this.slideHeight - this.heightTillModelInfo) / 2
  }

  getFontSize(fontSize: number) {
    if (this.toFitScale > 1) {
      return fontSize
    }
    else {
      return Math.ceil(fontSize * (this.toFitScale + (this.toFitScale / 2)))
    }
  }

  findTheScalingFactor(totalArticles: number, scale: number, width: number) {
    const eachCardWidth = this.imageSize
    this.toFitScale = scale - 0.02
    const newWidth = eachCardWidth * this.toFitScale
    const maximumNumberOCardsInOneRow = Math.floor(width / newWidth)
    if (maximumNumberOCardsInOneRow >= totalArticles || scale <= 0.2) {
      if (scale < 0.2) {
        this.needOverLapping = true
        this.toFitScale = 0.2
      }
    }
    else {
      this.findTheScalingFactor(totalArticles, this.toFitScale, width)
    }
  }

  findLeftForImages(numberOfImages: number) {
    const widthRequired = this.imageSize * numberOfImages
    const extraWidth = this.slideWidth - widthRequired
    return extraWidth / 2
  }

  onNewPage(currentObjects: any[], article: MyArticle) {
    currentObjects.length = 0
    currentObjects.push(this.getHeaderLogo())
    const title = this.slideTitleProps.title != null ? this.slideTitleProps.title.toString() : ''
    currentObjects.push(this.createStaticTextObj(title, 20, this.logoWidth, this.slideTitleProps.fontSize, this.slideTitleProps.fontWeight, '#000000', this.slideWidth - this.logoWidth - 10, this.slideTitleProps.height))
    currentObjects.push(this.createLabel(20 + this.slideTitleProps.height + 5, this.logoWidth, this.slideSubTitleProps.attributes, this.slideWidth - this.logoWidth - 10, this.slideSubTitleProps.height, article, this.slideSubTitleProps.fontSize, this.slideSubTitleProps.fontWeight, false))
  }

  addModelDetails(article: MyArticle, currentTop: number) {
    const left = 10
    const textHeaderTop = currentTop
    // calculate the width on time by drawing the text directly on slide
    const modelTextWidth = 90
    const articleNameTextWidth = 135
    const msrpTextWidth = 70
    const marginTextWidth = 73
    const articleNameHeight = 25
    return [
      this.createStaticTextObj('Model #:', textHeaderTop, left, this.modelInfoProps.fontSize, 'bold', '#000000', modelTextWidth, this.modelInfoProps.height),
      this.createLabel(textHeaderTop, left + modelTextWidth + 5, ['ModelNumber'], 100, this.modelInfoProps.height, article, this.modelInfoProps.fontSize, 'normal', false),
      this.createStaticTextObj('Model Name:', textHeaderTop + this.modelInfoProps.height, left, this.modelInfoProps.fontSize, 'bold', '#000000', articleNameTextWidth, this.modelInfoProps.height),
      this.createLabel(textHeaderTop + this.modelInfoProps.height, left + articleNameTextWidth, ['ArticleName'], 600, this.modelInfoProps.fontSize, article, this.modelInfoProps.fontSize, 'normal', false),
      this.createStaticTextObj('MSRP:', textHeaderTop + (articleNameHeight + this.modelInfoProps.height), left, this.modelInfoProps.fontSize, 'bold', '#000000', msrpTextWidth, this.modelInfoProps.height),
      this.createLabel(textHeaderTop + (articleNameHeight + this.modelInfoProps.height), left + msrpTextWidth, ['_RetailPrice'], 100, this.modelInfoProps.height, article, this.modelInfoProps.fontSize, 'normal', false),
      this.createStaticTextObj('Margin:', textHeaderTop + (2 * this.modelInfoProps.height) + articleNameHeight, left, this.modelInfoProps.fontSize, 'bold', '#000000', marginTextWidth, this.modelInfoProps.height),
      this.createStaticTextObj('', textHeaderTop + (2 * this.modelInfoProps.height) + articleNameHeight, left + marginTextWidth, this.modelInfoProps.fontSize, 'normal', '#000000', 300, this.modelInfoProps.height, false, false),
    ]
  }

  createStaticTextObj(text: string, top: number, left: number, fontSize: number, fontWeight: string, fontColor: string, width = 325, height = 200, templateObject = true, locked = true) {
    return {
      type: 'textbox',
      top,
      left,
      width,
      height,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text,
      fill: fontColor,
      backgroundColor: '',
      fontFamily: 'Neue Plak',
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign: 'left',
      locked,
      templateObject,
      isOriginTopLeft: true,
    }
  }

  createImage(articleId: number, imageType: string, left: number, top: number) {
    top = top + this.imageSize / 2
    return {
      type: 'articleImage',
      top,
      left: left + this.imageSize / 2,
      width: this.imageWidth,
      height: this.imageHeight,
      displayWidth: this.imageWidth,
      displayHeight: this.imageHeight,
      articleId,
      angle: 0,
      scaleX: this.imageSize / this.imageWidth,
      scaleY: this.imageSize / this.imageHeight,
      flipX: false,
      flipY: false,
      locked: false,
      templateObject: false,
      shouldAllowTemplateBigImagesToResize: true,
      imageType,
    }
  }

  createLabel(top: number, left: number, attributes: string[], width: number, height: number, article: MyArticle, fontSize: number, fontWeight: string, showLabels: boolean = true, textAlign: string = 'left') {
    if (!attributes || attributes.length === 0) { return }

    return {
      type: 'articleDetails',
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      fill: '#000000',
      backgroundColor: '',
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: attributes.slice(),
        articleBLCoords: { x: 0, y: 0 },
      },
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      locked: false,
      articleId: article.Id,
      templateObject: false,
      showLabels,
      isOriginTopLeft: true,
    }
  }

  createSlide(merch, name: string, objects: any[], options: Record<string, any>, articleIdList) {
    let newName = name && name !== '' ? name : 'Slide'
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === name))) {
      let c = 0
      do {
        c++
        newName = `${name} (${c})`
      } while (folderNode.children.find(node => node.label === newName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, newName, objects, options.slideSize, this.imageDefaultScaleFactor, sortOrder, true, articleIdList)
  }

  getHeaderLogo() {
    return {
      type: 'image',
      top: 44,
      left: 70,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 0,
      scaleY: this.logoScaling,
      scaleX: this.logoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: 'data:image/png;base64,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',

    }
  }

  getInfoLogo(left: number, top: number) {
    return {
      type: 'image',
      top,
      left,
      width: 30,
      height: 30,
      angle: 0,
      scaleX: 0.64,
      scaleY: 0.64,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAaCAYAAABYQRdDAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsQAAA7EAZUrDhsAAAHwSURBVEhL7ZTNbhJRFIC/ufPDDDMMP2KloZVURKmRAm1ZWIzW1IUbN30Cn8+F72BiXLjQR6ALUxO1uihNMcKMZwwIFKYkDe76JZM7d+beb86cc+/VQoElo4btUrmWLp/p6sttGAykHfYXoklYEpfSort/TEgHBGff6X14y2nnhP55n1CbHDpChv+dYWKWd/BqFZxiZuqXJ6QBQfcrv96/5subd5x9/slAgp5BRmuaAW6RzOErbjyp466lpiKd+IBCWS6JcgXT6ROcHNPrdOgdHc1ex98Y6CtYxQJmOjkljJgulEhV+SnZdgO3lEfXFZrkbHxJ7gwbPbeO9+wlfrWAldKHk8dcqH7UdUjuPSfVqGJnjah2Y6SjuXnMe3vkX2yRyLozUUZckEZoqPwmXmsXv1ZCD4PhYoiSmcRY38Q/OCB1y0E35ynnSgUrjX2/SWq3ibtqSw1FOAjQciWchy0yrQqGLamY74yRymN9pYxTa5FulDCULAOVxLpblw9t4615IowxCjFSQaK1Nh7gP27j5nRUtoLb3JFc38GM+e0R8dIo2nQBe+sRmfY2/v4+fr1K8qY3fB/P5Yd0KOu1+4PzTx/5bayS2LiNXcjG5nLEwpM/HIj4tEuoDJRtywaR3bSAhdKrcElOr861dPn8Byn8AbaUh1iB0h7lAAAAAElFTkSuQmCC',
    }
  }
}

export default new DevelopmentReview()
